/**
 * Comparison Table - Simple & Clean
 * Handles product selection for desktop popup and mobile dropdown
 */

(function() {
  'use strict';

  // ============================================================================
  // Configuration
  // ============================================================================
  const MOBILE_WIDTH = 768;
  
  /**
   * Field names used in data attributes and JavaScript.
   * These are shortened versions of the schema setting IDs for consistency and brevity.
   * 
   * Mapping:
   * - Schema ID → Data Attribute → JS Field
   * - depth → data-depth → 'depth'
   * - firmness_rating → data-firmness → 'firmness'
   * - springs → data-springs → 'springs'
   * - supported_weight → data-weight → 'weight'
   * - temperature_regulating → data-temp → 'temp'
   * - anti_motion_transfer → data-motion → 'motion'
   * - plush_quilted_cover → data-plush → 'plush'
   * - bamboo_charcoal → data-bamboo → 'bamboo'
   * - uk_made → data-uk → 'uk'
   * - hypoallergenic → data-hypo → 'hypo'
   * - from_price → data-price → 'price'
   */
  const FIELDS = ['depth', 'firmness', 'springs', 'weight', 'temp', 'motion', 'plush', 'bamboo', 'uk', 'hypo', 'price'];
  const BOOLEAN_FIELDS = ['temp', 'plush', 'bamboo', 'uk', 'hypo'];

  let currentPosition = null;
  const STORAGE_KEY = 'comparison-table-selections';

  // ============================================================================
  // Helpers
  // ============================================================================
  const isMobile = () => window.innerWidth <= MOBILE_WIDTH;
  
  const saveSelections = () => {
    const list = getActiveList();
    if (!list) return;
    
    const selections = {};
    list.querySelectorAll('.comparison-table__header-list-item').forEach(item => {
      const position = item.dataset.position;
      const handle = item.dataset.productHandle;
      if (position && handle) {
        selections[position] = handle;
      }
    });
    
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selections));
    } catch (e) {
      // Ignore localStorage errors
    }
  };
  
  const restoreSelections = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return;
      
      const selections = JSON.parse(saved);
      
      // Find all available products (from popup or dropdown)
      const allProducts = document.querySelectorAll('.comparison-table__popup-product, .comparison-table__mobile-dropdown-product');
      
      // Restore to both desktop and mobile lists
      const desktopList = document.querySelector('.comparison-table__header-list--desktop');
      const mobileList = document.querySelector('.comparison-table__header-list--mobile');
      
      Object.keys(selections).forEach(position => {
        const handle = selections[position];
        const productElement = Array.from(allProducts).find(p => p.dataset.product === handle);
        if (!productElement) return;
        
        // Update both lists
        [desktopList, mobileList].forEach(list => {
          if (!list) return;
          const gridItem = list.querySelector(`[data-position="${position}"]`);
          if (!gridItem) return;
          
          // Get data from selected product
          const name = productElement.querySelector('[class*="product-name"]').textContent;
          const image = productElement.dataset.image;
          const url = productElement.dataset.url || `/products/${handle}`;

          // Update UI
          const title = gridItem.querySelector('.comparison-table__popup-trigger-text');
          const img = gridItem.querySelector('.comparison-table__product-image-img');
          const link = gridItem.querySelector('.comparison-table__product-link');
          const trigger = gridItem.querySelector('.comparison-table__popup-trigger');
          
          if (title) title.textContent = name;
          if (img && image) img.src = image;
          if (link) link.href = url;
          
          // Update badge
          if (trigger) {
            const hasBadge = productElement.dataset.badge === 'true';
            trigger.classList.toggle('has-badge', hasBadge);
          }
          
          // Update data attributes
          gridItem.dataset.productHandle = handle;
          FIELDS.forEach(field => {
            gridItem.dataset[field] = productElement.dataset[field] || '';
          });
        });
      });
      
      // Update table after restoring
      updateTable();
    } catch (e) {
      // Ignore localStorage errors
    }
  };
  
  const getActiveList = () => {
    return isMobile() 
      ? document.querySelector('.comparison-table__header-list--mobile')
      : document.querySelector('.comparison-table__header-list--desktop');
  };

  const formatBoolean = (value) => {
    if (value === 'true' || value === true) return 'yes';
    if (value === 'false' || value === false) return 'no';
    return value || '-';
  };

  // ============================================================================
  // Core - Update Product
  // ============================================================================
  function updateProduct(selectedElement, position) {
    const list = getActiveList();
    if (!list) return;
    
    const gridItem = list.querySelector(`[data-position="${position}"]`);
    if (!gridItem) return;

    // Get data from selected product
    const handle = selectedElement.dataset.product;
    const name = selectedElement.querySelector('[class*="product-name"]').textContent;
    const image = selectedElement.dataset.image;
    const url = selectedElement.dataset.url || `/products/${handle}`;

    // Update UI
    const title = gridItem.querySelector('.comparison-table__popup-trigger-text');
    const img = gridItem.querySelector('.comparison-table__product-image-img');
    const link = gridItem.querySelector('.comparison-table__product-link');
    const trigger = gridItem.querySelector('.comparison-table__popup-trigger');
    
    if (title) title.textContent = name;
    if (img && image) img.src = image;
    if (link) link.href = url;
    
    // Update badge
    if (trigger) {
      const hasBadge = selectedElement.dataset.badge === 'true';
      trigger.classList.toggle('has-badge', hasBadge);
    }
    
    // Update data attributes
    gridItem.dataset.productHandle = handle;
    FIELDS.forEach(field => {
      gridItem.dataset[field] = selectedElement.dataset[field] || '';
    });

    updateTable();
    saveSelections();
  }

  // ============================================================================
  // Core - Update Table
  // ============================================================================
  function updateTable() {
    const list = getActiveList();
    if (!list) return;
    
    // Query all cells once and cache them for efficient lookup
    const allCells = document.querySelectorAll('.comparison-rows__cell');
    const cellCache = new Map();
    
    allCells.forEach(cell => {
      const field = cell.dataset.field;
      const position = cell.dataset.position;
      if (field && position) {
        const key = `${field}-${position}`;
        cellCache.set(key, cell);
      }
    });
    
    list.querySelectorAll('.comparison-table__header-list-item').forEach(item => {
      const position = item.dataset.position;
      
      FIELDS.forEach(field => {
        const key = `${field}-${position}`;
        const cell = cellCache.get(key);
        
        if (cell) {
          const value = item.dataset[field];
          const isBoolean = BOOLEAN_FIELDS.includes(field);
          
          if (isBoolean) {
            const formatted = formatBoolean(value);
            
            // Remove boolean state classes safely
            cell.classList.remove('comparison-rows__cell--yes', 'comparison-rows__cell--no');
            
            if (formatted === 'yes') {
              cell.classList.add('comparison-rows__cell--yes');
              cell.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="20" height="20" rx="10" fill="#68D794"/><path d="M14.3334 6.75L8.37502 12.7083L5.66669 10" stroke="black" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Yes</span>';
            } else if (formatted === 'no') {
              cell.classList.add('comparison-rows__cell--no');
              cell.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="20" height="20" rx="10" fill="#D61414"/><path d="M13.25 6.75L6.75 13.25M6.75 6.75L13.25 13.25" stroke="white" stroke-linecap="round" stroke-linejoin="round"/></svg><span>No</span>';
            } else {
              cell.innerHTML = '';
              cell.textContent = '-';
            }
          } else {
            // Clear any existing HTML content before setting text
            cell.innerHTML = '';
            cell.textContent = value || '-';
          }
        }
      });
    });
  }

  // ============================================================================
  // Selector (Popup/Dropdown) - Desktop as base, mobile adds small extras
  // ============================================================================
  const toggleChevron = (gridItem, add) => {
    // Remove active state from all items first
    document.querySelectorAll('.comparison-table__header-list-item.dropdown-active').forEach(item => {
      item.classList.remove('dropdown-active');
    });
    // Add active state to specific item if provided
    if (add && gridItem) {
      gridItem.classList.add('dropdown-active');
    }
  };

  function openSelector(position, trigger) {
    currentPosition = position;
    
    // Remove is-open class from all triggers first
    document.querySelectorAll('.comparison-table__popup-trigger.is-open').forEach(t => {
      t.classList.remove('is-open');
    });
    
    // Add is-open class to current trigger
    if (trigger) {
      trigger.classList.add('is-open');
    }
    
    // Get current product handle from the grid item
    const list = getActiveList();
    const gridItem = list?.querySelector(`[data-position="${position}"]`);
    const currentProductHandle = gridItem?.dataset.productHandle;
    
    if (isMobile()) {
      // Mobile: dropdown wrapper + chevron rotation
      const wrapper = document.querySelector('.comparison-table__mobile-dropdown-wrapper');
      if (!wrapper) return;
      
      if (wrapper.classList.contains('active') && wrapper.dataset.currentPosition === String(position)) {
        closeSelector();
        return;
      }
      
      // Clear previous selections
      document.querySelectorAll('.comparison-table__mobile-dropdown-product.selected').forEach(p => {
        p.classList.remove('selected');
      });
      
      // Pre-select current product if exists
      if (currentProductHandle) {
        const currentProduct = wrapper.querySelector(`[data-product="${currentProductHandle}"]`);
        if (currentProduct) {
          currentProduct.classList.add('selected');
        }
      }
      
      toggleChevron(trigger.closest('.comparison-table__header-list-item'), true);
      
      // Add class to header to allow dropdown expansion
      const header = document.querySelector('.comparison-table__header');
      if (header) {
        header.classList.add('dropdown-active');
      }
      
      wrapper.classList.add('active');
      wrapper.dataset.currentPosition = position;
      
      // Scroll to show dropdown content
      // Wait for dropdown animation to complete
      const scrollToDropdown = () => {
        // Double-check wrapper is active before scrolling
        if (!wrapper || !header || !wrapper.classList.contains('active')) return;
        
        // Get positions after dropdown has expanded
        const wrapperRect = wrapper.getBoundingClientRect();
        const headerRect = header.getBoundingClientRect();
        const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
        
        // Calculate the absolute document position of dropdown wrapper top
        const wrapperTopAbsolute = wrapperRect.top + currentScroll;
        
        // Get the sticky header's top offset from viewport (e.g., 65px on mobile)
        const stickyTopOffset = headerRect.top;
        
        // Calculate target scroll position
        // We want the dropdown to appear just below the sticky header with padding
        const targetScroll = wrapperTopAbsolute - stickyTopOffset - 20; // 20px padding from header
        
        window.scrollTo({
          top: Math.max(0, targetScroll),
          behavior: 'smooth'
        });
      };
      
      // Scroll after dropdown animation has completed
      // Use double requestAnimationFrame + timeout to ensure DOM has updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(scrollToDropdown, 100);
        });
      });
    } else {
      // Desktop: popup (base logic)
      // Clear previous selections
      document.querySelectorAll('.comparison-table__popup-product.selected').forEach(p => {
        p.classList.remove('selected');
      });
      
      // Pre-select current product if exists
      if (currentProductHandle) {
        const currentProduct = document.querySelector(`.comparison-table__popup-product[data-product="${currentProductHandle}"]`);
        if (currentProduct) {
          currentProduct.classList.add('selected');
        }
      }
      
      document.querySelector('.comparison-table__popup-overlay')?.classList.add('active');
      document.querySelector('.comparison-table__popup')?.classList.add('active');
      document.body.style.overflow = 'hidden';
    }
  }

  function closeSelector() {
    // Remove is-open class from all triggers
    document.querySelectorAll('.comparison-table__popup-trigger.is-open').forEach(t => {
      t.classList.remove('is-open');
    });
    
    // Desktop: close popup (base logic)
    document.querySelector('.comparison-table__popup-overlay')?.classList.remove('active');
    document.querySelector('.comparison-table__popup')?.classList.remove('active');
    document.body.style.overflow = '';
    document.querySelectorAll('.comparison-table__popup-product.selected').forEach(p => {
      p.classList.remove('selected');
    });
    
    // Mobile: close dropdown (small addition)
    const wrapper = document.querySelector('.comparison-table__mobile-dropdown-wrapper');
    if (wrapper) {
      wrapper.classList.remove('active');
      delete wrapper.dataset.currentPosition;
      toggleChevron(null, false);
      
      // Remove class from header
      const header = document.querySelector('.comparison-table__header');
      if (header) {
        header.classList.remove('dropdown-active');
      }
    }
    
    currentPosition = null;
  }

  function applySelection() {
    const selected = document.querySelector('.comparison-table__popup-product.selected');
    if (selected && currentPosition) {
      updateProduct(selected, currentPosition);
    }
    closeSelector();
  }

  // ============================================================================
  // Event Handlers
  // ============================================================================
  function init() {
    // Open selector (edit button / chevron)
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('.comparison-table__popup-trigger');
      if (trigger) {
        openSelector(trigger.dataset.position, trigger);
        // Remove focus on mobile to prevent persistent focus state
        if (isMobile()) {
          trigger.blur();
        }
      }
    });

    // Select product in popup (desktop)
    document.querySelectorAll('.comparison-table__popup-product').forEach(product => {
      product.addEventListener('click', () => {
        document.querySelectorAll('.comparison-table__popup-product').forEach(p => {
          p.classList.remove('selected');
        });
        product.classList.add('selected');
      });
    });

    // Select product in dropdown (mobile) - applies immediately
    document.querySelectorAll('.comparison-table__mobile-dropdown-product').forEach(product => {
      product.addEventListener('click', () => {
        if (currentPosition) {
          updateProduct(product, currentPosition);
          closeSelector();
        }
      });
    });

    // Apply button (desktop)
    const applyBtn = document.querySelector('.comparison-table__popup-apply');
    if (applyBtn) {
      applyBtn.addEventListener('click', applySelection);
    }

    // Close button (desktop)
    const closeBtn = document.querySelector('.comparison-table__popup-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', closeSelector);
    }

    // Overlay click (desktop)
    const overlay = document.querySelector('.comparison-table__popup-overlay');
    if (overlay) {
      overlay.addEventListener('click', closeSelector);
    }

    // Click outside dropdown (mobile)
    document.addEventListener('click', (e) => {
      const wrapper = document.querySelector('.comparison-table__mobile-dropdown-wrapper');
      if (!wrapper?.classList.contains('active')) return;
      
      const isInside = e.target.closest('.comparison-table__mobile-dropdown-wrapper');
      const isTrigger = e.target.closest('.comparison-table__popup-trigger');
      
      if (!isInside && !isTrigger) {
        closeSelector();
      }
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeSelector();
      }
    });

    // Initial table update
    updateTable();
    
    // Restore saved selections
    restoreSelections();
  }

  // ============================================================================
  // Initialize
  // ============================================================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API
  window.ComparisonTable = { updateTable, openSelector, closeSelector };

})();
